import { getLeadContactTier } from '@/lib/contact-vault/lead-contact-readiness';
import { normalizeEmail } from '@/lib/contact-vault/normalize';
import type { Lead } from '@/types/leads';

export const RECONCILE_NURTURE_TAG = '[reconcile:nurture]';
export const RECONCILE_DUPLICATE_TAG = '[reconcile:duplicate]';

export type ReconcileLead = Pick<
  Lead,
  | 'id'
  | 'email'
  | 'business_name'
  | 'dba'
  | 'lead_score'
  | 'contact_person'
  | 'contact_resolved_at'
  | 'status'
  | 'deal_id'
  | 'matched_entity_type'
  | 'matched_entity_id'
  | 'qa_notes'
  | 'suppression_reason'
>;

export type NurtureDealOwner = {
  dealId: string;
  dealName: string;
  email: string;
};

export type ReconcileDisqualifyAction = {
  leadId: string;
  reason: 'nurture' | 'duplicate';
  keepLeadId?: string;
  dealId?: string;
  dealName?: string;
  qa_notes: string;
  suppression_reason: 'inbound_dup' | 'other';
  matched_entity_type: 'open_deal' | 'existing_lead';
  matched_entity_id: string;
};

export type ReconcilePlan = {
  nurtureActions: ReconcileDisqualifyAction[];
  duplicateActions: ReconcileDisqualifyAction[];
  keptDuplicateLeadIds: string[];
  alreadyHandled: number;
  nurtureReviewLeadIds: string[];
  duplicateReviewGroups: string[][];
};

const terminalStatuses = new Set(['disqualified', 'suppressed', 'do_not_contact', 'converted']);

export function appendQaNote(existing: string | null | undefined, note: string): string {
  const current = existing?.trim() ?? '';
  if (!current) return note;
  if (current.includes(note)) return current;
  return `${current}\n${note}`;
}

export function alreadyReconciled(lead: Pick<ReconcileLead, 'qa_notes' | 'status'>, tag: string): boolean {
  if (terminalStatuses.has(lead.status)) return true;
  return Boolean(lead.qa_notes?.includes(tag));
}

export function scoreDuplicateKeeper(lead: ReconcileLead): number {
  let score = lead.lead_score ?? 0;
  if (lead.contact_resolved_at) score += 20;
  if (getLeadContactTier(lead) === 'named') score += 15;
  if (lead.deal_id) score += 5;
  const name = (lead.dba?.trim() || lead.business_name?.trim() || '').length;
  score += Math.min(name / 20, 5);
  return score;
}

export function chooseDuplicateKeeper(leads: ReconcileLead[]): ReconcileLead {
  if (leads.length === 0) {
    throw new Error('Cannot choose keeper from empty lead group');
  }
  return [...leads].sort(
    (left, right) =>
      scoreDuplicateKeeper(right) - scoreDuplicateKeeper(left) || left.id.localeCompare(right.id)
  )[0]!;
}

export function buildNurtureReconcileActions(
  _leads: ReconcileLead[],
  _nurtureOwnersByEmail: Map<string, NurtureDealOwner>
): ReconcileDisqualifyAction[] {
  return [];
}

/**
 * Among active leads sharing the same email, keep the strongest card and
 * disqualify the rest as inbound duplicates.
 */
export function buildDuplicateReconcileActions(
  _leads: ReconcileLead[],
  _options: { skipLeadIds?: Set<string> } = {}
): { actions: ReconcileDisqualifyAction[]; keptLeadIds: string[] } {
  return { actions: [], keptLeadIds: [] };
}

export function buildReconcilePlan(
  leads: ReconcileLead[],
  nurtureOwnersByEmail: Map<string, NurtureDealOwner>
): ReconcilePlan {
  const nurtureActions = buildNurtureReconcileActions(leads, nurtureOwnersByEmail);
  const nurtureLeadIds = new Set(nurtureActions.map((action) => action.leadId));
  const { actions: duplicateActions, keptLeadIds } = buildDuplicateReconcileActions(leads, {
    skipLeadIds: nurtureLeadIds,
  });

  const alreadyHandled = leads.filter(
    (lead) =>
      alreadyReconciled(lead, RECONCILE_NURTURE_TAG) ||
      alreadyReconciled(lead, RECONCILE_DUPLICATE_TAG)
  ).length;
  const nurtureReviewLeadIds = leads
    .filter((lead) => {
      const email = normalizeEmail(lead.email);
      return Boolean(email && nurtureOwnersByEmail.has(email));
    })
    .map((lead) => lead.id);
  const groups = new Map<string, string[]>();
  for (const lead of leads) {
    const email = normalizeEmail(lead.email);
    if (!email || terminalStatuses.has(lead.status)) continue;
    const ids = groups.get(email) ?? [];
    ids.push(lead.id);
    groups.set(email, ids);
  }
  const duplicateReviewGroups = [...groups.values()].filter((ids) => ids.length > 1);

  return {
    nurtureActions,
    duplicateActions,
    keptDuplicateLeadIds: keptLeadIds,
    alreadyHandled,
    nurtureReviewLeadIds,
    duplicateReviewGroups,
  };
}
